import { css, StyleSheet } from 'aphrodite'
import PropTypes from 'prop-types'
import { ChevronLeft, ChevronRight } from 'react-feather'
import {
  COLOR_GRAY_LIGHT,
  COLOR_GRAY__400,
  COLOR_GRAY__600,
  COLOR_GRAY__800,
  COLOR_GREEN__DARK,
  COLOR_GREEN__LIGHT,
  COLOR_PRIMARY,
  COLOR_PRIMARY__DARK,
  COLOR_PRIMARY__LIGHT,
  COLOR_SECONDARY__DARK,
  COLOR_TURQUOISE,
  COLOR_TURQUOISE__LIGHT,
  COLOR_WHITE,
} from '../../lib/colors'

function UIButton({
  label,
  disabled = false,
  fullWidth = false,
  kind = 'button',
  withForwardChevron = false,
  withBackwardChevron = false,
  design,
  type = 'button',
  smaller = false,
  dynamicStyle,
  textStyle,
  height,
  noSpan,
  ...props
}) {
  const hasForwardChevron = withForwardChevron && kind === 'button'
  const hasBackwardChevron = withBackwardChevron && kind === 'button'

  return (
    <button
      type={type}
      style={{ height: height }}
      className={css(
        styles.default,
        styles[kind],
        design ? styles[design] : null,
        disabled && [styles.disabled, styles[`${kind}Disabled`]],
        fullWidth && styles.fullWidth,
        smaller && styles.smaller,
        dynamicStyle,
        noSpan && textStyle ? textStyle : null
      )}
      disabled={disabled}
      {...props}
    >
      {hasBackwardChevron && (
        <span className={css(styles.chevron)}>
          <ChevronLeft color={COLOR_WHITE} size={15} />
        </span>
      )}
      {noSpan ? (
        label
      ) : (
        <span className={css(textStyle ? textStyle : null)}>{label}</span>
      )}
      {hasForwardChevron && (
        <span className={css(styles.chevron)}>
          <ChevronRight color={COLOR_WHITE} size={15} />
        </span>
      )}
    </button>
  )
}

const styles = StyleSheet.create({
  default: {
    fontSize: 14,
    border: 0,
    cursor: 'pointer',
    display: 'flex',
  },
  button: {
    padding: '16px 24px',
    borderRadius: 4,
    color: COLOR_WHITE,
    fontWeight: '600',
  },
  text: {
    backgroundColor: 'transparent',
    textDecoration: 'underline',
    fontWeight: '400',
    ':focus': {
      color: COLOR_GRAY__800,
    },
    ':hover': {
      color: COLOR_GRAY__600,
    },
  },
  primary: {
    borderRadius: 5,
    border: '2px solid #4C6FFF',
    color: COLOR_WHITE,
    backgroundColor: COLOR_PRIMARY,
    ':focus': {
      backgroundColor: COLOR_PRIMARY__DARK,
    },
    ':hover': {
      backgroundColor: COLOR_PRIMARY__LIGHT,
    },
  },
  secondary: {
    borderRadius: 5,
    border: '2px solid #B9AA00',
    color: COLOR_WHITE,
    backgroundColor: COLOR_GREEN__DARK,
    ':focus': {
      backgroundColor: COLOR_GREEN__DARK,
    },
    ':hover': {
      backgroundColor: COLOR_GREEN__LIGHT,
    },
  },
  tertiary: {
    borderRadius: 5,
    border: '2px solid #00A6CD',
    backgroundColor: COLOR_TURQUOISE,
    color: COLOR_WHITE,
    ':focus': {
      backgroundColor: COLOR_TURQUOISE,
    },
    ':hover': {
      backgroundColor: COLOR_TURQUOISE__LIGHT,
    },
  },
  tertiaryLight: {
    borderRadius: 5,
    backgroundColor: COLOR_TURQUOISE__LIGHT,
    color: COLOR_WHITE,
    ':focus': {
      backgroundColor: COLOR_TURQUOISE,
    },
    ':hover': {
      backgroundColor: COLOR_TURQUOISE,
    },
  },
  smaller: {
    padding: '6px 41px',
    borderRadius: 5,
    fontSize: 12,
  },
  disabled: {
    cursor: 'not-allowed',
  },
  buttonDisabled: {
    backgroundColor: COLOR_SECONDARY__DARK,
    ':focus': {
      backgroundColor: COLOR_SECONDARY__DARK,
    },
    ':hover': {
      backgroundColor: COLOR_SECONDARY__DARK,
    },
  },
  textDisabled: {
    color: COLOR_GRAY__400,
    ':focus': {
      color: COLOR_GRAY__400,
    },
    ':hover': {
      color: COLOR_GRAY__400,
    },
  },
  simpleText: {
    color: COLOR_GRAY_LIGHT,
    textDecorationLine: 'none',
    ':focus': {
      color: COLOR_GRAY__400,
    },
    ':hover': {
      color: COLOR_GRAY__400,
    },
  },
  chevron: {
    display: 'inline-block',
    margin: '0 10px',
    verticalAlign: 'middle',
  },
  fullWidth: {
    display: 'block',
    width: '100%',
  },
  invertedPrimary: {
    backgroundColor: 'transparent',
    border: '2px solid #4C6FFF',
    color: COLOR_PRIMARY,
    borderRadius: 5,
    textDecorationLine: 'none',
  },
  invertedSecondary: {
    backgroundColor: 'transparent',
    border: '2px solid #B9AA00',
    color: COLOR_GREEN__DARK,
    borderRadius: 5,
    textDecorationLine: 'none',
  },
  invertedTertiary: {
    backgroundColor: 'transparent',
    border: '2px solid #00A6CD',
    color: COLOR_TURQUOISE,
    borderRadius: 5,
    textDecorationLine: 'none',
  },
})

UIButton.propTypes = {
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  kind: PropTypes.oneOf(['button', 'text']),
  withForwardChevron: PropTypes.bool,
  withBackwardChevron: PropTypes.bool,
  onClick: PropTypes.func,
  design: PropTypes.oneOf([
    'primary',
    'secondary',
    'tertiary',
    'tertiaryLight',
    'invertedPrimary',
    'invertedSecondary',
    'invertedTertiary',
    'simpleText',
  ]),
  type: PropTypes.string,
  smaller: PropTypes.bool,
  dynamicStyle: PropTypes.object,
  textStyle: PropTypes.object,
  height: PropTypes.number,
  noSpan: PropTypes.bool,
}

UIButton.defaultProps = {
  noSpan: false,
}

export default UIButton
